import {
  AlertTriangle,
  ArrowDown,
  ArrowUp,
  ArrowUpDown,
  Download,
  Loader2,
  MousePointerClick,
  Search,
  Target,
  TrendingUp,
  Wallet,
} from "lucide-react";
import type { ReactNode } from "react";
import { useCallback, useEffect, useMemo, useState } from "react";
import { ChatLayoutWrapper, type ChatContext } from "../ai-chat";
import { ChannelTable, type ChannelRow } from "./ChannelBreakdown";
import { CpaChart } from "./company/CpaChart";
import { CompanyMarketingFunnel } from "./company/CompanyMarketingFunnel";
import type { Period } from "../funnel-detail/types";
import type { CompanyData, FunnelMonthlyRow } from "./company/types";
import type { Funnel } from "./funnels/types";
import { campaignCsvRows, downloadCsv } from "./financeCsv";
import {
  financeService,
  getCompanyFinanceSummary,
  getFinanceMetaConnection,
  type FinanceMetaCampaignPerformance,
  type FinanceMetaConnectionResponse,
} from "../../services/financeService";

type AdsTabProps = {
  period: Period;
};

type DecisionStatus = "scale" | "review" | "pause" | "connect" | "wait";

type MarketingFunnelRow = {
  id: string;
  name: string;
  productName: string;
  campaignCount: number;
  spend: number;
  impressions: number;
  clicks: number;
  orders: number;
  revenue: number;
  profit: number;
  cpa: number;
  roas: number;
  ctr: number;
  matchedCampaignIds: string[];
  campaigns: FinanceMetaCampaignPerformance[];
};

type MarketingDecisionRow = {
  id: string;
  element: string;
  context: string;
  status: DecisionStatus;
  statusLabel: string;
  motive: string;
  action: string;
  confidence: number;
  impact: number;
  priority: number;
  funnelId?: string;
};

type CampaignSortKey = "name" | "funnel" | "spend" | "revenue" | "profit" | "clicks" | "ctr" | "purchases" | "roas";
type SortDirection = "asc" | "desc";
type CampaignSort = {
  key: CampaignSortKey;
  direction: SortDirection;
};

const ALL_FUNNELS_FILTER = "all";
const UNMATCHED_FUNNEL_FILTER = "__unmatched__";

const formatCurrency = (value: number, currency = "COP") =>
  new Intl.NumberFormat("es-CO", {
    style: "currency",
    currency,
    maximumFractionDigits: 0,
  }).format(value || 0);

const formatNumber = (value: number) =>
  new Intl.NumberFormat("es-CO", { maximumFractionDigits: 0 }).format(value || 0);

const formatPercent = (value: number) =>
  `${new Intl.NumberFormat("es-CO", { maximumFractionDigits: 1 }).format(value || 0)}%`;

const formatMultiplier = (value: number) =>
  `${new Intl.NumberFormat("es-CO", { minimumFractionDigits: 1, maximumFractionDigits: 1 }).format(value || 0)}x`;

const decisionStatusDotClasses: Record<DecisionStatus, string> = {
  scale: "bg-emerald-500",
  review: "bg-amber-400",
  pause: "bg-red-500",
  connect: "bg-[#2b5ce6]",
  wait: "bg-slate-400",
};

const decisionStatusTextClasses: Record<DecisionStatus, string> = {
  scale: "text-emerald-700",
  review: "text-amber-700",
  pause: "text-red-700",
  connect: "text-[#2b5ce6]",
  wait: "text-slate-600",
};

const periodToDateRange = (period: Period) => ({
  initialDate: period.from,
  finalDate: period.to,
  label: period.label,
});

export const AdsTab = ({ period }: AdsTabProps) => {
  const [connection, setConnection] = useState<FinanceMetaConnectionResponse | null>(null);
  const [campaigns, setCampaigns] = useState<FinanceMetaCampaignPerformance[]>([]);
  const [funnels, setFunnels] = useState<Funnel[]>([]);
  const [companyData, setCompanyData] = useState<CompanyData | null>(null);
  const [search, setSearch] = useState("");
  const [campaignFunnelFilter, setCampaignFunnelFilter] = useState(ALL_FUNNELS_FILTER);
  const [campaignSort, setCampaignSort] = useState<CampaignSort>({ key: "spend", direction: "desc" });
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  const loadData = useCallback(
    async (showLoading = true) => {
      if (showLoading) setLoading(true);
      setError(null);

      try {
        const nextConnection = await getFinanceMetaConnection();
        setConnection(nextConnection);

        if (!nextConnection.connected) {
          setCampaigns([]);
          setFunnels([]);
          setCompanyData(null);
          return;
        }

        const [campaignRows, funnelRows, companySummary] = await Promise.all([
          financeService.getFinanceCampaignPerformance(period),
          financeService.getFunnels(periodToDateRange(period)),
          getCompanyFinanceSummary(period),
        ]);
        setCampaigns(campaignRows);
        setFunnels(funnelRows);
        setCompanyData(companySummary);
      } catch {
        setError("No pudimos cargar la información de Marketing.");
      } finally {
        if (showLoading) setLoading(false);
      }
    },
    [period]
  );

  useEffect(() => {
    loadData();
  }, [loadData]);

  const funnelRowsById = useMemo(() => {
    const rows = new Map<string, FunnelMonthlyRow>();
    companyData?.funnels.forEach((row) => rows.set(row.funnelId, row));
    return rows;
  }, [companyData?.funnels]);

  const accountScopedCampaigns = campaigns;

  const marketingFunnels = useMemo<MarketingFunnelRow[]>(() => {
    return funnels.map((funnel) => {
      const matchedCampaigns = accountScopedCampaigns.filter((campaign) => {
        const backendMatch = campaign.matched_funnel_ids?.includes(funnel.id);
        const localMatch = funnel.matchedCampaignIds.includes(campaign.id);
        return backendMatch || localMatch;
      });
      const companyRow = funnelRowsById.get(funnel.id);
      const spendFromCampaigns = matchedCampaigns.reduce((sum, row) => sum + Number(row.spend ?? 0), 0);
      const spend = Number(companyRow?.adInvestment ?? spendFromCampaigns);
      const orders = Number(funnel.metrics.salesCount ?? 0);
      const revenue = Number(companyRow?.revenue ?? 0);
      const profit = Number(companyRow?.profit ?? funnel.metrics.profit ?? 0);
      const impressions = matchedCampaigns.reduce((sum, row) => sum + Number(row.impressions ?? 0), 0);
      const clicks = matchedCampaigns.reduce((sum, row) => sum + Number(row.clicks ?? 0), 0);

      return {
        id: funnel.id,
        name: funnel.name,
        productName: funnel.productName,
        campaignCount: matchedCampaigns.length,
        spend,
        impressions,
        clicks,
        orders,
        revenue,
        profit,
        cpa: orders > 0 ? spend / orders : Number(funnel.metrics.cpa ?? 0),
        roas: spend > 0 ? revenue / spend : 0,
        ctr: impressions > 0 ? (clicks / impressions) * 100 : 0,
        matchedCampaignIds: matchedCampaigns.map((campaign) => campaign.id),
        campaigns: matchedCampaigns,
      };
    });
  }, [accountScopedCampaigns, funnelRowsById, funnels]);

  const connectedCampaignIds = useMemo(() => {
    const ids = new Set<string>();
    marketingFunnels.forEach((funnel) => funnel.matchedCampaignIds.forEach((id) => ids.add(id)));
    return ids;
  }, [marketingFunnels]);

  const funnelByCampaignId = useMemo(() => {
    const map = new Map<string, MarketingFunnelRow>();
    marketingFunnels.forEach((funnel) => {
      funnel.matchedCampaignIds.forEach((campaignId) => {
        map.set(campaignId, funnel);
      });
    });
    return map;
  }, [marketingFunnels]);

  const unmatchedCampaigns = useMemo(
    () => accountScopedCampaigns.filter((campaign) => !connectedCampaignIds.has(campaign.id)),
    [accountScopedCampaigns, connectedCampaignIds]
  );

  const connectedCampaigns = useMemo(
    () => accountScopedCampaigns.filter((campaign) => connectedCampaignIds.has(campaign.id)),
    [accountScopedCampaigns, connectedCampaignIds]
  );

  const metrics = useMemo(() => {
    const connectedSpend = marketingFunnels.reduce((sum, row) => sum + row.spend, 0);
    const connectedRevenue = marketingFunnels.reduce((sum, row) => sum + row.revenue, 0);
    const connectedOrders = marketingFunnels.reduce((sum, row) => sum + row.orders, 0);
    const connectedImpressions = marketingFunnels.reduce((sum, row) => sum + row.impressions, 0);
    const connectedClicks = marketingFunnels.reduce((sum, row) => sum + row.clicks, 0);
    const grossCampaignSpend = accountScopedCampaigns.reduce((sum, row) => sum + Number(row.spend ?? 0), 0);
    const grossMetaSpend = Number(companyData?.totalAdInvestment ?? grossCampaignSpend);
    const unmatchedSpendFromCampaigns = unmatchedCampaigns.reduce((sum, row) => sum + Number(row.spend ?? 0), 0);
    const unassignedSpend = Number(companyData?.unassignedAdInvestment ?? unmatchedSpendFromCampaigns);

    return {
      connectedSpend,
      connectedRevenue,
      connectedOrders,
      unassignedSpend,
      totalSpend: grossMetaSpend,
      roas: connectedSpend > 0 ? connectedRevenue / connectedSpend : 0,
      cpa: connectedOrders > 0 ? connectedSpend / connectedOrders : 0,
      connectedClicks,
      ctr: connectedImpressions > 0 ? (connectedClicks / connectedImpressions) * 100 : 0,
      connectedFunnels: marketingFunnels.filter((row) => row.campaignCount > 0).length,
      unmatchedCampaigns: unmatchedCampaigns.filter((row) => Number(row.spend ?? 0) > 0).length,
    };
  }, [
    accountScopedCampaigns,
    companyData?.totalAdInvestment,
    companyData?.unassignedAdInvestment,
    marketingFunnels,
    unmatchedCampaigns,
  ]);

  const sortedFunnels = useMemo(
    () =>
      [...marketingFunnels].sort((a, b) => {
        if (a.campaignCount !== b.campaignCount) return b.campaignCount - a.campaignCount;
        if (a.profit !== b.profit) return b.profit - a.profit;
        if (a.revenue !== b.revenue) return b.revenue - a.revenue;
        return b.spend - a.spend;
      }),
    [marketingFunnels]
  );

  const visibleCampaigns = useMemo(() => {
    const normalizedSearch = search.trim().toLowerCase();

    return accountScopedCampaigns
      .filter((campaign) => {
        const matchedFunnel = funnelByCampaignId.get(campaign.id);
        const matchesSearch = !normalizedSearch || campaign.name.toLowerCase().includes(normalizedSearch);
        const matchesFunnel =
          campaignFunnelFilter === ALL_FUNNELS_FILTER ||
          (campaignFunnelFilter === UNMATCHED_FUNNEL_FILTER && !matchedFunnel) ||
          matchedFunnel?.id === campaignFunnelFilter;

        return matchesSearch && matchesFunnel;
      })
      .sort((a, b) => compareCampaigns(a, b, funnelByCampaignId, campaignSort));
  }, [accountScopedCampaigns, campaignFunnelFilter, campaignSort, funnelByCampaignId, search]);

  const chatContext = useMemo<ChatContext>(() => {
    if (!connection?.connected) {
      return {
        surface: "ads",
        period: {
          id: period.id,
          label: period.label,
          from: period.from.toISOString().slice(0, 10),
          to: period.to.toISOString().slice(0, 10),
          days: period.days,
        },
        summary: "Fluxi Finance Marketing: Meta no está conectado.",
      };
    }

    return {
      surface: "ads",
      period: {
        id: period.id,
        label: period.label,
        from: period.from.toISOString().slice(0, 10),
        to: period.to.toISOString().slice(0, 10),
        days: period.days,
      },
      summary: [
        "Fluxi Finance Marketing",
        `Business: ${connection.business_name ?? "sin nombre"}`,
        `Periodo: ${period.label}`,
        `Pauta total: ${formatCurrency(metrics.totalSpend)}`,
        `Pauta sin embudo: ${formatCurrency(metrics.unassignedSpend)}`,
        `CPA: ${formatCurrency(metrics.cpa)}`,
        `ROAS: ${formatMultiplier(metrics.roas)}`,
        `Embudos conectados: ${metrics.connectedFunnels}`,
      ].join("\n"),
    };
  }, [connection, metrics, period]);

  if (loading) {
    return (
      <div className="flex min-h-[360px] items-center justify-center text-sm font-medium text-gray-500">
        <Loader2 className="mr-2 h-4 w-4 animate-spin" />
        Cargando marketing...
      </div>
    );
  }

  return (
    <ChatLayoutWrapper context={chatContext}>
      <div className="space-y-5">
        {error ? (
          <div className="rounded-lg border border-red-100 bg-red-50 px-4 py-3 text-sm font-medium text-red-700">
            {error}
          </div>
        ) : null}

        {!connection?.connected ? (
          <MetaRequiredState />
        ) : (
          <>
            <MarketingSummary metrics={metrics} />

            {companyData ? (
              <CompanyMarketingFunnel data={companyData} period={period} />
            ) : (
              <MarketingFunnelPlaceholder />
            )}

            <CpaChart
              dailyData={companyData?.dailyData ?? []}
              cpaTargets={companyData?.cpaTargets ?? { realCpa: 0, targetCpa20: 0, breakEvenCpa: 0 }}
              period={period}
            />

            <CampaignsWorkspace
              campaigns={visibleCampaigns}
              totalCampaigns={accountScopedCampaigns.length}
              search={search}
              onSearchChange={setSearch}
              funnelFilter={campaignFunnelFilter}
              onFunnelFilterChange={setCampaignFunnelFilter}
              funnelRows={marketingFunnels}
              sort={campaignSort}
              onSortChange={setCampaignSort}
            />

            <MarketingChannelsSection
              campaigns={accountScopedCampaigns}
              connectedCampaigns={connectedCampaigns}
              unconnectedCampaigns={unmatchedCampaigns}
              companyData={companyData}
            />
          </>
        )}
      </div>
    </ChatLayoutWrapper>
  );
};

function MetaRequiredState() {
  return (
    <div id="finance-marketing-source-target" className="rounded-lg border border-blue-100 bg-white px-6 py-8 shadow-sm">
      <div className="flex items-start gap-4">
        <div className="flex h-12 w-12 shrink-0 items-center justify-center rounded-lg bg-blue-50 text-[#2b5ce6]">
          <AlertTriangle className="h-6 w-6" />
        </div>
        <div>
          <h2 className="text-lg font-semibold text-gray-900">Marketing todavía no tiene Meta conectado</h2>
          <p className="mt-2 max-w-2xl text-sm leading-6 text-gray-500">
            Usa el botón de configuración junto al filtro de fecha para conectar o sincronizar. Cuando esté conectado, esta hoja
            mostrará campañas, CPA, ROAS y el cruce con embudos.
          </p>
        </div>
      </div>
    </div>
  );
}

function EmptyDataState() {
  return (
    <div id="finance-marketing-next-step-target" className="rounded-lg border border-gray-100 bg-white px-6 py-8 shadow-sm">
      <div>
        <h3 className="text-base font-semibold text-gray-900">Todavía no hay datos de marketing</h3>
        <p className="mt-2 max-w-2xl text-sm leading-6 text-gray-500">
          Sincroniza Meta Ads desde configuración para traer campañas, inversión y cruces con embudos.
        </p>
      </div>
    </div>
  );
}

function MarketingSummary({
  metrics,
}: {
  metrics: {
    connectedSpend: number;
    connectedRevenue: number;
    connectedOrders: number;
    unassignedSpend: number;
    totalSpend: number;
    roas: number;
    cpa: number;
    connectedClicks: number;
    ctr: number;
  };
}) {
  const cards = [
    {
      label: "Pauta Fluxi",
      value: formatCurrency(metrics.connectedSpend),
      detail: "Pauta asociada a embudos",
      icon: Wallet,
    },
    {
      label: "CPA",
      value: formatCurrency(metrics.cpa),
      detail: `${formatNumber(metrics.connectedOrders)} órdenes conectadas`,
      icon: Target,
    },
    {
      label: "ROAS",
      value: formatMultiplier(metrics.roas),
      detail: `${formatCurrency(metrics.connectedRevenue)} recaudo conectado`,
      icon: TrendingUp,
    },
    {
      label: "CTR",
      value: formatPercent(metrics.ctr),
      detail: `${formatNumber(metrics.connectedClicks)} clics conectados`,
      icon: MousePointerClick,
    },
  ];

  return (
    <div id="finance-marketing-summary-target" className="grid gap-4 md:grid-cols-2 xl:grid-cols-4">
      {cards.map((card) => {
        const Icon = card.icon;
        return (
          <div key={card.label} className="rounded-lg border border-gray-200 bg-white px-5 py-4 shadow-sm">
            <div className="flex items-center justify-between gap-3">
              <p className="text-[11px] font-semibold uppercase text-gray-500">{card.label}</p>
              <Icon className="h-4 w-4 text-gray-400" />
            </div>
            <p className="mt-2 text-2xl font-bold tabular-nums text-gray-900">
              {card.value}
            </p>
            <p className="mt-1 text-xs text-gray-500">{card.detail}</p>
          </div>
        );
      })}
    </div>
  );
}

function MarketingFunnelPlaceholder() {
  return (
    <section className="rounded-lg border border-gray-200 bg-white p-5 shadow-sm">
      <h2 className="text-base font-semibold text-gray-900">Funnel consolidado de marketing</h2>
      <div className="mt-4 flex h-[240px] items-center justify-center rounded-lg border border-dashed border-gray-200 bg-gray-50 text-sm font-medium text-gray-400">
        Sin datos consolidados para este periodo
      </div>
    </section>
  );
}

function MarketingDecisionCenter({
  funnels,
  unmatchedCampaigns,
  onOpenFunnel,
}: {
  funnels: MarketingFunnelRow[];
  unmatchedCampaigns: FinanceMetaCampaignPerformance[];
  onOpenFunnel: (funnelId: string) => void;
}) {
  const [decisionFilter, setDecisionFilter] = useState<"all" | "review" | "scale" | "pause">("all");
  const connectedFunnels = funnels.filter((funnel) => funnel.campaignCount > 0);
  const unmatchedWithSpend = unmatchedCampaigns
    .filter((campaign) => Number(campaign.spend ?? 0) > 0)
    .sort((a, b) => Number(b.spend ?? 0) - Number(a.spend ?? 0));
  const decisionRows = buildMarketingDecisionRows(connectedFunnels, unmatchedWithSpend);
  const visibleDecisionRows = decisionRows.filter((row) => {
    if (decisionFilter === "all") return true;
    return row.status === decisionFilter;
  });

  return (
    <section id="finance-marketing-funnels-target" className="rounded-lg border border-gray-100 bg-white shadow-sm">
      <div className="flex flex-col gap-2 border-b border-gray-100 px-5 py-4 md:flex-row md:items-end md:justify-between">
        <div>
          <h3 className="text-base font-semibold text-gray-900">Centro de decisión</h3>
          <p className="mt-1 text-sm text-gray-500">
            Prioriza qué escalar, qué revisar y qué conectar sin revisar campaña por campaña.
          </p>
        </div>
        <p className="text-xs font-semibold uppercase text-gray-400">
          {connectedFunnels.length} de {funnels.length} embudos con pauta
        </p>
      </div>

      <div className="p-5">
        <div className="mb-4 flex flex-wrap gap-2">
          {[
            { id: "all", label: "Todas" },
            { id: "review", label: "Revisar" },
            { id: "scale", label: "Escalar" },
            { id: "pause", label: "Apagar" },
          ].map((option) => (
            <button
              key={option.id}
              type="button"
              onClick={() => setDecisionFilter(option.id as typeof decisionFilter)}
              className={`inline-flex h-8 items-center justify-center rounded-full border px-3 text-xs font-bold transition ${
                decisionFilter === option.id
                  ? "border-[#2b5ce6] bg-blue-50 text-[#2b5ce6]"
                  : "border-gray-200 bg-white text-gray-500 hover:bg-gray-50"
              }`}
            >
              {option.label}
            </button>
          ))}
        </div>
        <div className="overflow-x-auto rounded-lg border border-gray-100">
          <table className="min-w-[860px] divide-y divide-gray-100 lg:min-w-full">
            <thead className="bg-gray-50">
              <tr>
                <TableHead>Campaña</TableHead>
                <TableHead>Estado</TableHead>
                <TableHead>Motivo</TableHead>
                <TableHead>Acción sugerida</TableHead>
                <TableHead align="right">Confianza</TableHead>
              </tr>
            </thead>
            <tbody className="divide-y divide-gray-100 bg-white">
              {visibleDecisionRows.map((row) => (
                <tr
                  key={row.id}
                  onClick={row.funnelId ? () => onOpenFunnel(row.funnelId!) : undefined}
                  className={row.funnelId ? "cursor-pointer transition hover:bg-blue-50/40" : undefined}
                >
                  <td className="max-w-[280px] px-5 py-4">
                    <p className="truncate text-sm font-semibold text-gray-900">{row.element}</p>
                    <p className="mt-1 truncate text-xs text-gray-400">{row.context}</p>
                  </td>
                  <td className="px-5 py-4">
                    <span className={`inline-flex items-center gap-2 text-sm font-bold ${decisionStatusTextClasses[row.status]}`}>
                      <span className={`h-3 w-3 rounded-full shadow-sm ${decisionStatusDotClasses[row.status]}`} />
                      {row.statusLabel}
                    </span>
                  </td>
                  <td className="max-w-[360px] px-5 py-4 text-sm leading-5 text-gray-600">{row.motive}</td>
                  <td className="max-w-[360px] px-5 py-4 text-sm font-semibold leading-5 text-gray-900">{row.action}</td>
                  <td className="whitespace-nowrap px-5 py-4 text-right">
                    <p className="text-sm font-bold text-gray-900">{row.confidence}%</p>
                    <p className="mt-1 text-xs font-medium text-gray-400">{confidenceLabel(row.confidence)}</p>
                  </td>
                </tr>
              ))}
              {visibleDecisionRows.length === 0 ? (
                <tr>
                  <td colSpan={5} className="px-5 py-8 text-center text-sm text-gray-500">
                    No hay decisiones para este filtro.
                  </td>
                </tr>
              ) : null}
            </tbody>
          </table>
        </div>
      </div>
    </section>
  );
}

function buildMarketingDecisionRows(
  connectedFunnels: MarketingFunnelRow[],
  unmatchedCampaigns: FinanceMetaCampaignPerformance[]
): MarketingDecisionRow[] {
  const connectedCampaignRows = connectedFunnels
    .flatMap((funnel) => funnel.campaigns.map((campaign) => buildCampaignDecision(campaign, funnel)))
    .filter((row, index, allRows) => allRows.findIndex((candidate) => candidate.id === row.id) === index);
  const connectRows = unmatchedCampaigns
    .slice(0, 2)
    .map((campaign) => {
      const spend = Number(campaign.spend ?? 0);
      return {
        id: `connect-${campaign.id}`,
        element: campaign.name,
        context: `Sin embudo · ${campaign.effective_status ?? "Sin estado"}`,
        status: "connect" as DecisionStatus,
        statusLabel: "Conectar",
        motive: "Tiene inversión, pero Finance no puede leer su impacto en un embudo.",
        action: "Conectarla al embudo correcto o separarla si es campaña de awareness.",
        confidence: campaignConfidence(campaign),
        impact: spend,
        priority: 55,
      };
    });

  const rows = [...connectedCampaignRows, ...connectRows]
    .filter((row, index, allRows) => allRows.findIndex((candidate) => candidate.id === row.id) === index)
    .sort((a, b) => b.priority - a.priority || b.impact - a.impact)
    .slice(0, 8);

  if (rows.length > 0) return rows;

  return [
    {
      id: "wait-for-signal",
      element: "Marketing",
      context: "Sin campañas accionables",
      status: "wait",
      statusLabel: "Esperar",
      motive: "Aún falta señal suficiente entre pauta, embudos y recaudo.",
      action: "Sincronizar Meta y conectar campañas antes de tomar decisiones de presupuesto.",
      confidence: 58,
      impact: 0,
      priority: 0,
    },
  ];
}

function buildCampaignDecision(
  campaign: FinanceMetaCampaignPerformance,
  funnel: MarketingFunnelRow
): MarketingDecisionRow {
  const spend = Number(campaign.spend ?? 0);
  const clicks = Number(campaign.clicks ?? 0);
  const impressions = Number(campaign.impressions ?? 0);
  const purchases = Number(campaign.purchases ?? 0);
  const ctr = impressions > 0 ? (clicks / impressions) * 100 : 0;
  const revenue = Number(funnel.revenue || campaign.purchase_value || 0);
  const roas = spend > 0 ? revenue / spend : Number(campaign.roas ?? 0);
  const profit = revenue > 0 ? revenue - spend : funnel.profit;
  const base = {
    id: `campaign-${campaign.id}`,
    element: campaign.name,
    context: `${funnel.name} · ${campaign.effective_status ?? "Sin estado"}`,
    confidence: campaignConfidence(campaign),
    impact: spend + Math.abs(profit),
    priority: 90,
    funnelId: funnel.id,
  };

  if (spend > 0 && clicks === 0) {
    return {
      ...base,
      status: "review",
      statusLabel: "Revisar",
      motive: "Hay pauta invertida, pero no aparecen clics.",
      action: "Revisar audiencia, creativo y promesa antes de tocar el checkout.",
    };
  }

  if (spend > 0 && funnel.orders === 0) {
    return {
      ...base,
      status: "review",
      statusLabel: "Revisar",
      motive: `${formatNumber(clicks)} clics y ninguna orden registrada en ${funnel.name}.`,
      action: "Validar match del embudo, página y checkout antes de escalar.",
    };
  }

  if (profit < 0) {
    return {
      ...base,
      status: "pause",
      statusLabel: "Apagar",
      motive: `${formatCurrency(profit)} de utilidad asociada al embudo.`,
      action: "Bajar pauta o corregir CPA, precio y costos antes de crecer.",
      priority: 95,
    };
  }

  if (spend > 0 && purchases > 0 && profit > 0 && roas >= 1) {
    return {
      ...base,
      status: "scale",
      statusLabel: "Escalar",
      motive: `${formatMultiplier(roas)} ROAS y ${formatCurrency(profit)} de utilidad asociada.`,
      action: "Subir presupuesto gradualmente y vigilar que el CPA no se deteriore.",
      priority: 70,
    };
  }

  return {
    ...base,
    status: "review",
    statusLabel: "Revisar",
    motive: `${formatPercent(ctr)} CTR y ${formatMultiplier(roas)} ROAS todavía no justifican escalar.`,
    action: "Revisar anuncio, oferta y continuidad con el embudo.",
    priority: 65,
  };
}

function campaignConfidence(campaign: FinanceMetaCampaignPerformance) {
  const spend = Number(campaign.spend ?? 0);
  if (spend >= 50000 && Number(campaign.clicks ?? 0) >= 50) return 82;
  if (spend > 0) return 68;
  return 55;
}

function confidenceLabel(confidence: number) {
  if (confidence >= 85) return "Alta";
  if (confidence >= 70) return "Media";
  return "Inicial";
}

function getCampaignTableMetrics(
  campaign: FinanceMetaCampaignPerformance,
  funnelByCampaignId: Map<string, MarketingFunnelRow>
) {
  const matchedFunnel = funnelByCampaignId.get(campaign.id);
  const spend = Number(campaign.spend ?? 0);
  const revenue = Number(matchedFunnel?.revenue ?? campaign.purchase_value ?? 0);
  const profit = matchedFunnel ? matchedFunnel.profit : revenue - spend;
  const clicks = Number(campaign.clicks ?? 0);
  const impressions = Number(campaign.impressions ?? 0);
  const purchases = Number(campaign.purchases ?? 0);
  const ctr = impressions > 0 ? (clicks / impressions) * 100 : 0;
  const roas = spend > 0 ? revenue / spend : Number(campaign.roas ?? 0);

  return {
    matchedFunnel,
    spend,
    revenue,
    profit,
    clicks,
    ctr,
    purchases,
    roas,
  };
}

function getCampaignSortValue(
  campaign: FinanceMetaCampaignPerformance,
  funnelByCampaignId: Map<string, MarketingFunnelRow>,
  key: CampaignSortKey
) {
  const metrics = getCampaignTableMetrics(campaign, funnelByCampaignId);
  if (key === "name") return campaign.name;
  if (key === "funnel") return metrics.matchedFunnel?.name ?? "Sin embudo";
  return metrics[key];
}

function compareCampaigns(
  campaignA: FinanceMetaCampaignPerformance,
  campaignB: FinanceMetaCampaignPerformance,
  funnelByCampaignId: Map<string, MarketingFunnelRow>,
  sort: CampaignSort
) {
  const valueA = getCampaignSortValue(campaignA, funnelByCampaignId, sort.key);
  const valueB = getCampaignSortValue(campaignB, funnelByCampaignId, sort.key);
  const direction = sort.direction === "asc" ? 1 : -1;

  if (typeof valueA === "string" || typeof valueB === "string") {
    const result = String(valueA).localeCompare(String(valueB), "es", { sensitivity: "base" });
    return result === 0 ? campaignA.name.localeCompare(campaignB.name, "es") : result * direction;
  }

  const result = valueA - valueB;
  return result === 0 ? campaignA.name.localeCompare(campaignB.name, "es") : result * direction;
}

function CampaignsWorkspace({
  campaigns,
  totalCampaigns,
  search,
  onSearchChange,
  funnelFilter,
  onFunnelFilterChange,
  funnelRows,
  sort,
  onSortChange,
}: {
  campaigns: FinanceMetaCampaignPerformance[];
  totalCampaigns: number;
  search: string;
  onSearchChange: (search: string) => void;
  funnelFilter: string;
  onFunnelFilterChange: (filter: string) => void;
  funnelRows: MarketingFunnelRow[];
  sort: CampaignSort;
  onSortChange: (sort: CampaignSort) => void;
}) {
  const exportMap = useMemo(() => {
    const map = new Map<string, string>();
    funnelRows.forEach((funnel) => {
      funnel.matchedCampaignIds.forEach((campaignId) => {
        map.set(campaignId, funnel.name);
      });
    });
    return map;
  }, [funnelRows]);
  const funnelFilterOptions = useMemo(
    () => [
      { value: ALL_FUNNELS_FILTER, label: "Todos los embudos" },
      { value: UNMATCHED_FUNNEL_FILTER, label: "Sin embudo" },
      ...[...funnelRows]
        .sort((a, b) => a.name.localeCompare(b.name, "es", { sensitivity: "base" }))
        .map((funnel) => ({ value: funnel.id, label: funnel.name })),
    ],
    [funnelRows]
  );
  const sortLabel = sort.direction === "asc" ? "menor a mayor" : "mayor a menor";

  return (
    <div id="finance-marketing-campaigns-target" className="rounded-lg border border-gray-100 bg-white shadow-sm">
      <div className="space-y-3 border-b border-gray-100 px-5 py-4">
        <div className="flex flex-col gap-3 md:flex-row md:items-center md:justify-between">
          <div>
            <h3 className="text-base font-semibold text-gray-900">Campañas</h3>
            <p className="mt-1 text-sm text-gray-500">
              Campañas filtradas y ordenadas por columna.
            </p>
          </div>
          <button
            type="button"
            onClick={() => downloadCsv("marketing-campanas.csv", campaignCsvRows(campaigns, exportMap))}
            className="inline-flex h-10 items-center justify-center gap-2 rounded-lg border border-gray-200 bg-white px-4 text-sm font-semibold text-gray-700 shadow-sm transition hover:bg-gray-50"
          >
            <Download className="h-4 w-4 text-[#2b5ce6]" />
            Descargar
          </button>
        </div>
        <div className="grid gap-2 md:grid-cols-[minmax(240px,1fr)_minmax(220px,280px)]">
          <label className="flex h-9 items-center gap-2 rounded-lg border border-gray-200 bg-white px-3 text-sm text-gray-500 focus-within:border-[#2b5ce6]">
            <Search className="h-4 w-4" />
            <input
              value={search}
              onChange={(event) => onSearchChange(event.target.value)}
              placeholder="Buscar campaña"
              className="min-w-0 flex-1 bg-transparent text-sm text-gray-700 outline-none placeholder:text-gray-400"
              type="search"
            />
          </label>
          <select
            value={funnelFilter}
            onChange={(event) => onFunnelFilterChange(event.target.value)}
            className="h-9 rounded-lg border border-gray-200 px-3 text-sm font-semibold text-gray-700 outline-none transition focus:border-[#2b5ce6]"
          >
            {funnelFilterOptions.map((option) => (
              <option key={option.value} value={option.value}>{option.label}</option>
            ))}
          </select>
        </div>
        <span className="text-xs font-medium text-gray-400">
          {campaigns.length} de {totalCampaigns} campañas · Orden: {campaignSortLabel(sort.key)} de {sortLabel}
        </span>
      </div>

      <CampaignsTable
        campaigns={campaigns}
        funnelRows={funnelRows}
        sort={sort}
        onSortChange={onSortChange}
      />
    </div>
  );
}

function CampaignsTable({
  campaigns,
  funnelRows,
  sort,
  onSortChange,
}: {
  campaigns: FinanceMetaCampaignPerformance[];
  funnelRows: MarketingFunnelRow[];
  sort: CampaignSort;
  onSortChange: (sort: CampaignSort) => void;
}) {
  const pageSize = 10;
  const [currentPage, setCurrentPage] = useState(1);
  const funnelNameByCampaignId = useMemo(() => {
    const map = new Map<string, MarketingFunnelRow>();
    funnelRows.forEach((funnel) => {
      funnel.matchedCampaignIds.forEach((campaignId) => {
        map.set(campaignId, funnel);
      });
    });
    return map;
  }, [funnelRows]);
  const totalPages = Math.max(1, Math.ceil(campaigns.length / pageSize));
  const visibleCampaigns = useMemo(() => {
    const safePage = Math.min(currentPage, totalPages);
    const start = (safePage - 1) * pageSize;
    return campaigns.slice(start, start + pageSize);
  }, [campaigns, currentPage, totalPages]);

  useEffect(() => {
    setCurrentPage(1);
  }, [campaigns.length, sort.direction, sort.key]);

  useEffect(() => {
    if (currentPage > totalPages) setCurrentPage(totalPages);
  }, [currentPage, totalPages]);

  const handleSortChange = (key: CampaignSortKey) => {
    const defaultDirection: SortDirection = key === "name" || key === "funnel" ? "asc" : "desc";
    onSortChange(
      sort.key === key
        ? { key, direction: sort.direction === "desc" ? "asc" : "desc" }
        : { key, direction: defaultDirection }
    );
  };

  if (campaigns.length === 0) {
    return (
      <div className="px-5 py-10 text-center text-sm text-gray-500">
        No hay campañas para este filtro.
      </div>
    );
  }

  return (
    <>
      <div className="overflow-x-auto">
        <table className="min-w-full divide-y divide-gray-100">
          <thead className="bg-gray-50">
            <tr>
              <SortableTableHead sortKey="name" sort={sort} onSortChange={handleSortChange}>Campaña</SortableTableHead>
              <SortableTableHead sortKey="funnel" sort={sort} onSortChange={handleSortChange}>Embudo</SortableTableHead>
              <SortableTableHead sortKey="spend" sort={sort} onSortChange={handleSortChange} align="right">Pauta</SortableTableHead>
              <SortableTableHead sortKey="revenue" sort={sort} onSortChange={handleSortChange} align="right">Recaudo</SortableTableHead>
              <SortableTableHead sortKey="profit" sort={sort} onSortChange={handleSortChange} align="right">Utilidad</SortableTableHead>
              <SortableTableHead sortKey="clicks" sort={sort} onSortChange={handleSortChange} align="right">Clics</SortableTableHead>
              <SortableTableHead sortKey="ctr" sort={sort} onSortChange={handleSortChange} align="right">CTR</SortableTableHead>
              <SortableTableHead sortKey="purchases" sort={sort} onSortChange={handleSortChange} align="right">Compras</SortableTableHead>
              <SortableTableHead sortKey="roas" sort={sort} onSortChange={handleSortChange} align="right">ROAS</SortableTableHead>
            </tr>
          </thead>
          <tbody className="divide-y divide-gray-100 bg-white">
            {visibleCampaigns.map((campaign) => {
              const {
                matchedFunnel,
                spend,
                revenue,
                profit,
                clicks,
                ctr,
                purchases,
                roas,
              } = getCampaignTableMetrics(campaign, funnelNameByCampaignId);

              return (
                <tr key={campaign.id}>
                  <td className="max-w-[360px] px-5 py-4">
                    <p className="truncate text-sm font-semibold text-gray-900">{campaign.name}</p>
                    <p className="mt-1 text-xs text-gray-400">{campaign.effective_status ?? "Sin estado"}</p>
                  </td>
                  <td className="max-w-[260px] px-5 py-4">
                    <span
                      className={
                        matchedFunnel
                          ? "inline-flex max-w-full rounded-full bg-blue-50 px-2.5 py-1 text-xs font-semibold text-[#2b5ce6]"
                          : "inline-flex rounded-full bg-amber-50 px-2.5 py-1 text-xs font-semibold text-amber-700"
                      }
                    >
                      <span className="truncate">{matchedFunnel?.name ?? "Sin embudo"}</span>
                    </span>
                  </td>
                  <TableCell>{formatCurrency(spend)}</TableCell>
                  <TableCell>{formatCurrency(revenue)}</TableCell>
                  <TableCell className={profit < 0 ? "text-red-700" : "text-emerald-700"}>
                    {formatCurrency(profit)}
                  </TableCell>
                  <TableCell>{formatNumber(clicks)}</TableCell>
                  <TableCell>{formatPercent(ctr)}</TableCell>
                  <TableCell>{formatNumber(purchases)}</TableCell>
                  <TableCell>{formatMultiplier(roas)}</TableCell>
                </tr>
              );
            })}
          </tbody>
        </table>
      </div>
      {campaigns.length > pageSize ? (
        <div className="flex flex-col gap-3 border-t border-gray-100 px-5 py-4 text-sm sm:flex-row sm:items-center sm:justify-between">
          <span className="font-medium text-gray-500">
            Mostrando {(Math.min(currentPage, totalPages) - 1) * pageSize + 1}-{Math.min(Math.min(currentPage, totalPages) * pageSize, campaigns.length)} de {campaigns.length}
          </span>
          <div className="flex items-center gap-2">
            <button
              type="button"
              onClick={() => setCurrentPage((page) => Math.max(1, page - 1))}
              disabled={currentPage <= 1}
              className="h-9 rounded-lg border border-gray-200 px-3 font-semibold text-gray-600 transition hover:bg-gray-50 disabled:cursor-not-allowed disabled:opacity-50"
            >
              Anterior
            </button>
            <span className="min-w-[92px] text-center text-xs font-bold text-gray-500">
              {Math.min(currentPage, totalPages)} / {totalPages}
            </span>
            <button
              type="button"
              onClick={() => setCurrentPage((page) => Math.min(totalPages, page + 1))}
              disabled={currentPage >= totalPages}
              className="h-9 rounded-lg border border-gray-200 px-3 font-semibold text-gray-600 transition hover:bg-gray-50 disabled:cursor-not-allowed disabled:opacity-50"
            >
              Siguiente
            </button>
          </div>
        </div>
      ) : null}
    </>
  );
}

function MarketingChannelsSection({
  campaigns,
  connectedCampaigns,
  unconnectedCampaigns,
  companyData,
}: {
  campaigns: FinanceMetaCampaignPerformance[];
  connectedCampaigns: FinanceMetaCampaignPerformance[];
  unconnectedCampaigns: FinanceMetaCampaignPerformance[];
  companyData: CompanyData | null;
}) {
  const allCampaignSpend = campaigns.reduce((sum, campaign) => sum + Number(campaign.spend ?? 0), 0);
  const connectedSpend = connectedCampaigns.reduce((sum, campaign) => sum + Number(campaign.spend ?? 0), 0);
  const unconnectedSpend = unconnectedCampaigns.reduce((sum, campaign) => sum + Number(campaign.spend ?? 0), 0);
  const totalSpend = Number(companyData?.totalAdInvestment ?? allCampaignSpend);
  const matchedSpend = Number(companyData?.matchedAdInvestment ?? connectedSpend);
  const unmatchedSpend = Number(companyData?.unassignedAdInvestment ?? unconnectedSpend);
  const currency = companyData?.fixedCostsCurrency ?? "COP";
  const rows: ChannelRow[] = [
    {
      label: "Conectada a Fluxi",
      detail: "Campañas con embudo asociado para leer impacto.",
      countLabel: "Campañas",
      count: connectedCampaigns.length,
      valueLabel: "Pauta",
      value: matchedSpend,
      share: totalSpend > 0 ? (matchedSpend / totalSpend) * 100 : 0,
      onDownload: () => downloadCsv("meta-conectada-fluxi.csv", campaignCsvRows(connectedCampaigns)),
    },
    {
      label: "Sin embudo",
      detail: "Pauta que Finance todavía no puede explicar con Fluxi.",
      countLabel: "Campañas",
      count: unconnectedCampaigns.length,
      valueLabel: "Pauta",
      value: unmatchedSpend,
      share: totalSpend > 0 ? (unmatchedSpend / totalSpend) * 100 : 0,
      onDownload: () => downloadCsv("meta-sin-embudo.csv", campaignCsvRows(unconnectedCampaigns)),
    },
    {
      label: "Total pauta Meta",
      detail: "Gasto bruto registrado en Meta Ads.",
      countLabel: "Campañas",
      count: campaigns.length,
      valueLabel: "Pauta",
      value: totalSpend,
      share: 100,
      variant: "total",
      onDownload: () => downloadCsv("meta-total-campanas.csv", campaignCsvRows(campaigns)),
    },
  ];

  return (
    <div id="finance-marketing-channels-target">
      <ChannelTable
        title="Canales de pauta"
        subtitle="Qué parte de la pauta tiene embudo asociado."
        rows={rows}
        currency={currency}
      />
    </div>
  );
}

function campaignSortLabel(key: CampaignSortKey) {
  const labels: Record<CampaignSortKey, string> = {
    name: "campaña",
    funnel: "embudo",
    spend: "pauta",
    revenue: "recaudo",
    profit: "utilidad",
    clicks: "clics",
    ctr: "CTR",
    purchases: "compras",
    roas: "ROAS",
  };
  return labels[key];
}

function SortableTableHead({
  children,
  sortKey,
  sort,
  onSortChange,
  align = "left",
}: {
  children: ReactNode;
  sortKey: CampaignSortKey;
  sort: CampaignSort;
  onSortChange: (key: CampaignSortKey) => void;
  align?: "left" | "right";
}) {
  const active = sort.key === sortKey;
  const Icon = !active ? ArrowUpDown : sort.direction === "desc" ? ArrowDown : ArrowUp;

  return (
    <th className="whitespace-nowrap px-5 py-3 text-xs font-bold uppercase tracking-normal text-gray-400">
      <button
        type="button"
        onClick={() => onSortChange(sortKey)}
        className={`inline-flex w-full items-center gap-1.5 transition hover:text-gray-700 ${
          align === "right" ? "justify-end text-right" : "justify-start text-left"
        } ${active ? "text-[#2b5ce6]" : ""}`}
        aria-label={`Ordenar por ${campaignSortLabel(sortKey)}`}
      >
        <span>{children}</span>
        <Icon className="h-3.5 w-3.5" />
      </button>
    </th>
  );
}

function TableHead({
  children,
  align = "left",
}: {
  children: ReactNode;
  align?: "left" | "right";
}) {
  return (
    <th
      className={`whitespace-nowrap px-5 py-3 text-xs font-bold uppercase tracking-normal text-gray-400 ${
        align === "right" ? "text-right" : "text-left"
      }`}
    >
      {children}
    </th>
  );
}

function TableCell({
  children,
  className = "",
}: {
  children: ReactNode;
  className?: string;
}) {
  return (
    <td className={`whitespace-nowrap px-5 py-4 text-right text-sm font-semibold text-gray-700 ${className}`}>
      {children}
    </td>
  );
}
